import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Copy, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Diálogo "Clonar plan a otro cliente".
 *
 * Props:
 *   - open: bool
 *   - onClose: () => void
 *   - plan: { id, name }
 *   - role: 'admin' | 'trainer'
 *   - onCloned: (newPlan) => void
 */
const ClonePlanDialog = ({ open, onClose, plan, role = 'trainer', onCloned }) => {
  const [newName, setNewName] = useState('');
  const [clients, setClients] = useState([]);
  const [assignTo, setAssignTo] = useState('none');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setNewName(plan ? `${plan.name} (copia)` : '');
    setAssignTo('none');
    fetchClients();
  }, [open, plan]);

  const fetchClients = async () => {
    setLoading(true);
    try {
      const endpoint = role === 'admin' ? `${API}/clients` : `${API}/trainer/clients`;
      const res = await axios.get(endpoint);
      setClients(res.data || []);
    } catch (e) {
      // no rompemos; el usuario puede clonar sin asignar
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!plan) return;
    setSubmitting(true);
    try {
      const endpoint = role === 'admin'
        ? `${API}/admin/plans/${plan.id}/clone`
        : `${API}/trainer/plans/${plan.id}/clone`;
      const body = {
        new_name: newName.trim() || null,
        assign_to_client_id: assignTo !== 'none' ? assignTo : null,
      };
      const res = await axios.post(endpoint, body);
      const assigneeName = assignTo !== 'none'
        ? (clients.find((c) => c.id === assignTo)?.name || 'cliente')
        : null;
      toast.success(
        assigneeName
          ? `Plan clonado y asignado a ${assigneeName}`
          : 'Plan clonado correctamente'
      );
      if (onCloned) onCloned(res.data);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al clonar el plan');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !submitting) onClose(); }}>
      <DialogContent className="bg-card border-border" data-testid="clone-plan-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Copy className="h-5 w-5 text-primary" />
            Clonar plan
          </DialogTitle>
          <DialogDescription>
            Crea una copia exacta de <strong>{plan?.name}</strong>. Puedes asignarla a un cliente
            directamente o editarla después.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label htmlFor="clone-name">Nombre del nuevo plan</Label>
            <Input
              id="clone-name"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Ej: Plan María (copia de Senior)"
              data-testid="clone-name-input"
              className="mt-1"
            />
          </div>

          <div>
            <Label htmlFor="clone-assign">Asignar a cliente (opcional)</Label>
            <Select value={assignTo} onValueChange={setAssignTo}>
              <SelectTrigger className="mt-1" data-testid="clone-assign-select">
                <SelectValue placeholder={loading ? 'Cargando...' : 'No asignar'} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No asignar (solo crear)</SelectItem>
                {clients.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name} · {c.email}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground mt-1">
              Si asignas, el cliente verá este plan al instante.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={submitting} data-testid="clone-submit-btn">
            {submitting ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Clonando...</>
            ) : (
              <><Copy className="h-4 w-4 mr-2" /> Clonar plan</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ClonePlanDialog;
